import { useState } from "react";
import { Sparkles, Copy, MessageSquare, Mail } from "lucide-react";
import api from "@/lib/api";

// AI-assisted reply drafting (Claude). Drafts only — nothing is sent.
export default function DraftReplyPanel({ lead }) {
    const [channel, setChannel] = useState("sms");
    const [instructions, setInstructions] = useState("");
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const generate = async () => {
        setBusy(true);
        setError("");
        try {
            let r;
            try {
                r = await api.post(`/admin/leads/${lead.id}/draft-reply`, { channel, instructions });
            } catch (first) {
                if (first?.response?.status !== 502) throw first;
                r = await api.post(`/admin/leads/${lead.id}/draft-reply`, { channel, instructions });
            }
            setDraft(r.data.draft);
        } catch (e) {
            setError(e?.response?.data?.detail || "Couldn't generate a draft. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!draft) return;
        try {
            await navigator.clipboard.writeText(draft);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable */ }
    };

    return (
        <div className="a1-card a1-card-feature space-y-4" data-testid="lead-draft-panel">
            <div className="flex items-center gap-2">
                <Sparkles size={18} className="text-[var(--color-accent)]" />
                <h3 className="text-base m-0">AI reply draft</h3>
            </div>
            <p className="a1-helper !mt-0">
                Drafts a reply to {lead.name || "this lead"} for you to review. Nothing is sent automatically — copy it into your own text or email.
            </p>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setChannel("sms")}
                    className={channel === "sms" ? "a1-btn-primary" : "a1-btn-secondary"}
                    data-testid="draft-channel-sms"
                >
                    <MessageSquare size={14} /> Text (SMS)
                </button>
                <button
                    type="button"
                    onClick={() => setChannel("email")}
                    className={channel === "email" ? "a1-btn-primary" : "a1-btn-secondary"}
                    data-testid="draft-channel-email"
                >
                    <Mail size={14} /> Email
                </button>
            </div>
            <input
                className="a1-input"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                maxLength={500}
                placeholder="Optional: anything to mention? (e.g., ask for photos of the door frame)"
                data-testid="draft-instructions"
            />
            <button
                type="button"
                onClick={generate}
                disabled={busy}
                className="a1-btn-primary"
                data-testid="draft-generate-btn"
            >
                <Sparkles size={14} /> {busy ? "Drafting…" : draft ? "Draft again" : "Generate draft"}
            </button>
            {error ? (
                <p className="text-sm" style={{ color: "var(--color-danger)" }} data-testid="draft-error">{error}</p>
            ) : null}
            {draft ? (
                <div className="space-y-2">
                    <textarea
                        className="a1-input w-full font-mono text-sm"
                        rows={channel === "email" ? 10 : 5}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        data-testid="draft-output"
                    />
                    <div className="flex gap-2 items-center">
                        <button type="button" onClick={copy} className="a1-btn-secondary" data-testid="draft-copy-btn">
                            <Copy size={14} /> {copied ? "Copied" : "Copy draft"}
                        </button>
                        <span className="a1-helper">Review before sending — no prices, no time promises.</span>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
